import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { PlanogramComplianceConfigItem } from '../../../types';

interface PlanogramComplianceAdvancedSettingsProps {
  config: PlanogramComplianceConfigItem[];
  defaultConfig?: PlanogramComplianceConfigItem[] | null;
  onConfigChange: (key: string, value: number | boolean | string | string[]) => void;
  onReset?: () => void;
  children?: React.ReactNode;
  isV2: boolean;
}

const TYPE_ORDER: Record<string, number> = { number: 0, boolean: 1, enum: 2, multi_enum: 3 };

export const PlanogramComplianceAdvancedSettings: React.FC<
  PlanogramComplianceAdvancedSettingsProps
> = ({ config, defaultConfig, onConfigChange, onReset, children, isV2 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isModified = useMemo(() => {
    if (!defaultConfig) return false;
    return config.some((item) => {
      const defaultItem = defaultConfig.find((d) => d.key === item.key);
      if (!defaultItem) return false;
      if (item.type === 'multi_enum') {
        const a = Array.isArray(item.value) ? item.value : [];
        const b = Array.isArray(defaultItem.value) ? defaultItem.value : [];
        if (a.length !== b.length) return true;
        const bSet = new Set(b);
        return a.some((v) => !bSet.has(v));
      }
      return item.value !== defaultItem.value;
    });
  }, [config, defaultConfig]);

  const sortedConfig = useMemo(
    () => [...config].sort((a, b) => (TYPE_ORDER[a.type] ?? 3) - (TYPE_ORDER[b.type] ?? 3)),
    [config]
  );

  const numberItems = sortedConfig.filter((item) => item.type === 'number');
  const booleanItems = useMemo(() => {
    const items = sortedConfig.filter((item) => item.type === 'boolean');
    const defaultTrue = (key: string) => {
      const source = defaultConfig ?? config;
      return source.find((d) => d.key === key)?.defaultValue === true;
    };
    return [...items].sort((a, b) => {
      const aTrue = defaultTrue(a.key);
      const bTrue = defaultTrue(b.key);
      if (aTrue === bTrue) return 0;
      return aTrue ? -1 : 1;
    });
  }, [sortedConfig, defaultConfig, config]);
  const enumItems = sortedConfig.filter(
    (item) => item.type === 'enum' || item.type === 'multi_enum'
  );

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Advanced Settings</span>
          {isModified && (
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Modified</span>
          )}
        </div>
        {isExpanded ? (
          <ExpandLessIcon className="text-gray-600" />
        ) : (
          <ExpandMoreIcon className="text-gray-600" />
        )}
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-4 space-y-6 bg-white">
          {children}
          <div className={`space-y-6 ${isV2 ? 'hidden' : ''}`}>
            {/* Number Inputs */}
            {numberItems.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {numberItems.map((item) => (
                  <div key={item.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {item.label}
                    </label>
                    <input
                      type="number"
                      value={(item.value as number) ?? ''}
                      onChange={(e) => {
                        const trimmed = e.target.value.trim();
                        const parsed = parseFloat(trimmed);
                        const numValue =
                          trimmed === '' || !Number.isFinite(parsed)
                            ? (item.defaultValue as number)
                            : parsed;
                        onConfigChange(item.key, numValue);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={String(item.defaultValue)}
                      step={Number(item.defaultValue) % 1 !== 0 ? '0.01' : '1'}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Boolean Toggles */}
            {booleanItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
                {booleanItems.map((item) => (
                  <div key={item.key} className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{item.label}:</span>
                    <button
                      type="button"
                      onClick={() => onConfigChange(item.key, !item.value)}
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                        item.value
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-gray-300 hover:border-gray-400'
                      }`}
                      title={`Toggle ${item.label}`}
                    >
                      {item.value && <CheckIcon sx={{ fontSize: 16 }} />}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Enum Selects */}
            {enumItems.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-4">
                {enumItems.map((item) => {
                  const isMulti = item.type === 'multi_enum';
                  const formatOption = (option: string) => {
                    const spaced = option.replace(/_/g, ' ');
                    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
                  };

                  if (isMulti) {
                    const selected = Array.isArray(item.value) ? item.value : [];
                    const handleChange = (e: SelectChangeEvent<string[]>) => {
                      const v = e.target.value;
                      onConfigChange(item.key, typeof v === 'string' ? v.split(',') : v);
                    };
                    return (
                      <div key={item.key} className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {item.label}
                        </label>
                        <FormControl size="small" fullWidth>
                          <Select
                            multiple
                            displayEmpty
                            value={selected}
                            onChange={handleChange}
                            renderValue={(picked) => {
                              if (picked.length === 0) {
                                return (
                                  <span className="text-gray-400">
                                    Select {item.label.toLowerCase()}...
                                  </span>
                                );
                              }
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {picked.map((value) => (
                                    <Chip key={value} label={formatOption(value)} size="small" />
                                  ))}
                                </div>
                              );
                            }}
                          >
                            {item.options?.map((option) => (
                              <MenuItem key={option} value={option}>
                                <Checkbox checked={selected.includes(option)} />
                                <ListItemText primary={formatOption(option)} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    );
                  }

                  const singleValue =
                    typeof item.value === 'string' || typeof item.value === 'number'
                      ? String(item.value)
                      : '';
                  return (
                    <div key={item.key} className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {item.label}
                      </label>
                      <select
                        value={singleValue}
                        onChange={(e) => onConfigChange(item.key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {singleValue === '' && (
                          <option value="" disabled>
                            Select {item.label.toLowerCase()}...
                          </option>
                        )}
                        {item.options?.map((option) => (
                          <option key={option} value={option}>
                            {formatOption(option)}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}

            {onReset && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onReset}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <RestartAltIcon fontSize="small" />
                  Reset to Defaults
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
